import { useToast } from "@atlas/core";
import { useTranslation } from "@atlas/sdk";
import { HubPackageStatus } from "@enums";
import type { IHubCart, IHubPackage } from "@models";
import {
  useCreateHubCart,
  useUpdateHubCart,
  useUpdateHubPackage,
} from "@query";
import { useCallback, useMemo } from "react";

export const useSwitchPackage = (userHubCart: IHubCart | undefined | null) => {
  const { t } = useTranslation();
  const toast = useToast();
  const { mutate: createHubCart, isPending: isCreateCartPending } =
    useCreateHubCart();
  const { mutate: updateHubCart, isPending: isUpdateCartPending } =
    useUpdateHubCart();
  const { mutate: updateHubPackage, isPending: isUpdatePkgPending } =
    useUpdateHubPackage();

  const isSwitchPending = useMemo(
    () => isCreateCartPending || isUpdateCartPending || isUpdatePkgPending,
    [isCreateCartPending, isUpdateCartPending, isUpdatePkgPending],
  );

  const switchPackage = useCallback(
    (
      pkg: IHubPackage,
      options?: { onSuccess?: () => void; onError?: () => void },
    ) => {
      const pkgId = pkg.packageId;
      if (pkgId == null || pkgId <= 0) return;

      const body: IHubCart = {
        packageId: pkgId,
      };

      const handleError = () => {
        toast.add({
          title: t("hub.package_manager.default_cart_error", {
            defaultValue: "Could not update default package",
          }),
          data: { type: "error" },
        });
        options?.onError?.();
      };

      const handlePackageSuccess = () => {
        toast.add({
          title: t("hub.package_manager.default_cart_success", {
            defaultValue: "Default package updated",
          }),
          data: { type: "success" },
        });

        // Add a tiny delay to ensure backend search indexes catch up with the new modifiedByDatetime
        setTimeout(() => {
          options?.onSuccess?.();
        }, 100);
      };

      const updatePkg = () => {
        updateHubPackage(
          {
            ...pkg,
            catalogRelations: pkg.catalogRelations ?? [],
            status: HubPackageStatus.Ready,
          },
          {
            onSuccess: handlePackageSuccess,
            onError: handleError,
          },
        );
      };

      if (userHubCart?.cartId == null) {
        createHubCart(body, {
          onSuccess: updatePkg,
          onError: handleError,
        });
      } else {
        updateHubCart(
          { ...userHubCart, ...body },
          {
            onSuccess: updatePkg,
            onError: handleError,
          },
        );
      }
    },
    [userHubCart, createHubCart, updateHubCart, updateHubPackage, toast, t],
  );

  return { switchPackage, isSwitchPending };
};
